import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { COLLECTION } from "../../../../data/constant";

const RANGE_OPTIONS = [7, 15, 30, 90, 180];
const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042",
  "#AA336A", "#663399", "#FF5555", "#33CC33",
];

const cardStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 150,
  fontFamily: "'Montserrat', sans-serif",
};

const parseTimestamp = (ts) => {
  if (!ts) return null;
  try {
    return ts?.toDate ? ts.toDate() : new Date(ts);
  } catch {
    return null;
  }
};

const formatDate = (date) => {
  if (!date) return "";
  return date.toISOString().split("T")[0];
};

const RechargeTab = () => {
  const [recharges, setRecharges] = useState([]);
  const [users, setUsers] = useState([]);
  const [walletDeductions, setWalletDeductions] = useState([]);
  const [userRange, setUserRange] = useState(30);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      const rechargeSnap = await getDocs(collection(db, COLLECTION.RECHARGES));
      setRecharges(rechargeSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const userSnap = await getDocs(collection(db, COLLECTION.USER));
      setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const walletSnap = await getDocs(collection(db, COLLECTION.WALLET_DEDUCTIONS));
      setWalletDeductions(walletSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchData();
  }, []);

  /* ---------------- FILTER SUCCESSFUL ---------------- */
  const successfulRecharges = useMemo(() =>
    recharges.filter(tx => tx && tx.message === "PAYMENT_SUCCESS" && Number(tx?.amount) > 0),
    [recharges]
  );

  /* ---------------- KPI CALCULATIONS ---------------- */
  const totalRechargeAmount = useMemo(() =>
    successfulRecharges.reduce((sum, tx) => sum + (Number(tx?.amount) || 0), 0),
    [successfulRecharges]
  );

  const rechargeCount = successfulRecharges.length;
  const avgRechargeAmount = rechargeCount > 0 ? totalRechargeAmount / rechargeCount : 0;

  const todaysRechargeAmount = useMemo(() => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return successfulRecharges
      .filter(tx => {
        const date = parseTimestamp(tx?.transactiontime);
        return date && date >= today && date < tomorrow;
      })
      .reduce((sum, tx) => sum + (Number(tx?.amount) || 0), 0);
  }, [successfulRecharges]);

  /* ---------------- ACTIVE USERS (FROM WALLET DEDUCTIONS) ---------------- */
  const activeUserPhones = useMemo(() => {
    const set = new Set();
    walletDeductions.forEach(wd => {
      const phone = String(wd?.phoneNumber || "").trim();
      if (phone && Number(wd?.amountDeducted) > 0) {
        set.add(phone);
      }
    });
    return set;
  }, [walletDeductions]);

  /* ---------------- POTENTIAL RECHARGE KPI ---------------- */
  const potentialRechargeAmount = useMemo(() => {
    const THRESHOLD = 100;
    let total = 0;

    users.forEach(user => {
      const phone = String(user?.phoneNumber || "").trim();
      if (!phone || !activeUserPhones.has(phone)) return;

      const walletBalance = Number(user?.walletAmount || 0);
      if (walletBalance < THRESHOLD) {
        total += (THRESHOLD - walletBalance);
      }
    });

    return total;
  }, [users, activeUserPhones]);

  /* ---------------- USER MAP ---------------- */
  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => {
      if (u && u.phoneNumber) map[String(u.phoneNumber)] = u.userName || "Unknown";
    });
    return map;
  }, [users]);

  /* ---------------- DAILY DATA ---------------- */
  const dailyData = useMemo(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - userRange + 1);
    startDate.setHours(0,0,0,0);
    today.setHours(23,59,59,999);

    const map = {};
    for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
      map[formatDate(new Date(d))] = { x: formatDate(new Date(d)), amount: 0, count: 0 };
    }

    successfulRecharges.forEach(tx => {
      if (!tx) return;
      const date = parseTimestamp(tx?.transactiontime);
      if (!date || date < startDate || date > today) return;

      const key = formatDate(date);
      if (!map[key]) map[key] = { x: key, amount: 0, count: 0 };
      map[key].amount += Number(tx?.amount) || 0;
      map[key].count += 1;
    });

    return Object.values(map);
  }, [successfulRecharges, userRange]);

  /* ---------------- TOP USERS ---------------- */
  const topUsers = useMemo(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - userRange + 1);
    startDate.setHours(0,0,0,0);
    today.setHours(23,59,59,999);

    const spendMap = {};
    successfulRecharges.forEach(tx => {
      if (!tx) return;
      const date = parseTimestamp(tx?.transactiontime);
      if (!date || date < startDate || date > today) return;

      const phone = String(tx?.phoneNumber || "").trim();
      if (!phone) return;

      if (!spendMap[phone]) {
        spendMap[phone] = { phone, name: userMap[phone] || "Unknown", amount: 0 };
      }
      spendMap[phone].amount += Number(tx?.amount) || 0;
    });

    return Object.values(spendMap)
      .filter(u => u && typeof u.amount === "number")
      .sort((a,b) => b.amount - a.amount)
      .slice(0, 10);
  }, [successfulRecharges, userMap, userRange]);

  return (
    <Box sx={{ mt: 2, fontFamily:"'Montserrat', sans-serif" }}>
      {/* KPI CARDS */}
      <Grid container spacing={2} justifyContent="space-between">
        {[
          ["Total Recharge Amount", `₹${totalRechargeAmount}`],
          ["Recharge Count", rechargeCount],
          ["Average Recharge Amount", `₹${avgRechargeAmount.toFixed(2)}`],
          ["Today's Recharge Amount", `₹${todaysRechargeAmount}`],
          ["Potential Recharge to Access Machine", `₹${potentialRechargeAmount}`],
        ].map(([label, value]) => (
          <Grid item xs="auto" key={label}>
            <Paper sx={cardStyle}>
              <Typography variant="subtitle2" color="textSecondary">{label}</Typography>
              <Typography variant="h6" sx={{ mt:0.5 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* GRAPHS */}
      <Grid container spacing={3} sx={{ mt:2 }}>
        {/* Total Recharge per Day */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:300 }}>
          <Paper sx={{ p:2, borderRadius:3, height:350 }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", mb:1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Total Recharge per Day</Typography>
              <FormControl size="small" sx={{ minWidth:80 }}>
                <Select value={userRange} onChange={e => setUserRange(+e.target.value)}>
                  {RANGE_OPTIONS.map(v => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip formatter={(v) => `₹${v}`} />
                <Line dataKey="amount" stroke="#1976d2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recharge Count Trend */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:300 }}>
          <Paper sx={{ p:2, borderRadius:3, height:350 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb:1 ,fontFamily: "Montserrat"}}>Recharge Count Trend</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Recharge Users */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:350 }}>
          <Paper sx={{ p:2, borderRadius:3, height:350 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb:1 , fontFamily: "Montserrat"}}>Top Recharge Users</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={topUsers}
                  dataKey="amount"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(e) => e.name}
                >
                  {topUsers.map((_,i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, _, p) => [`₹${v}`, p?.payload?.name || "Unknown"]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RechargeTab;
